// Free-form list of LOB / sub-brand names with a live count.

import { Plus, X } from 'lucide-react';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  /** Optional pill rendered in the header (e.g. derived "1–2 LOBs" bucket). */
  bucketLabel?: string;
}

export default function LobList({ value, onChange, bucketLabel }: Props) {
  const update = (i: number, v: string) => {
    const next = [...value];
    next[i] = v;
    onChange(next);
  };
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  const add = () => onChange([...value, `LOB ${value.length + 1}`]);

  const count = value.length;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="pill bg-slate-100 text-slate-700 tabular-nums">
          {count} {count === 1 ? 'LOB' : 'LOBs'}
        </span>
        {bucketLabel && (
          <span
            className="pill bg-brand-purple-50 text-brand-purple-700 border border-brand-purple-100"
            title="Bucket used to look up the LOB-count factor in the model"
          >
            Factor bucket: {bucketLabel}
          </span>
        )}
      </div>
      {value.length === 0 && (
        <p className="text-xs text-slate-400">No LOBs / sub-brands yet.</p>
      )}
      {value.map((lob, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-slate-400 w-6 text-right">{i + 1}.</span>
          <input
            className="input flex-1"
            value={lob}
            onChange={(e) => update(i, e.target.value)}
            placeholder={`LOB ${i + 1}`}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-slate-400 hover:text-rose-500"
            aria-label="Remove LOB"
          >
            <X size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="text-xs text-brand-purple-700 hover:text-brand-purple-900 inline-flex items-center gap-1"
      >
        <Plus size={12} /> Add LOB / sub-brand
      </button>
    </div>
  );
}
